import { ArrowLeft, ArrowRight, ChevronLeft } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Typography from "@mui/material/Typography";
import { Button } from "@components/ui/button";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import {
  getCicProfileById,
  upsertCicProfile,
} from "@redux/slices/strategicPlanning";
import useAuth from "@hooks/useAuth";
import LoadingScreen from "@components/LoadingScreen";
import { styled } from "@mui/material/styles";
import Stack from "@mui/material/Stack";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import icon1 from "../../../assets/icons/BuildingBlock1.svg";
import icon2 from "../../../assets/icons/BuildingBlock2.svg";
import icon3 from "../../../assets/icons/BuildingBlock3.svg";
import icon4 from "../../../assets/icons/BuildingBlock4.svg";
import { enqueueSnackbar } from "notistack";
import { Checkbox } from "@mui/material";
import { addNotification } from "@redux/slices/notifications";

import pdf from "../../../assets/Phase 4 Inhalt.pdf"

const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 28,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient(180deg, #13d6ce 0%, #039992 80%)",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: "linear-gradient(180deg, #13d6ce 0%, #039992 80%)",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "#eaeaf0",
    borderRadius: 1,
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
}));

const ColorlibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ theme }) => ({
  backgroundColor: "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 60,
  height: 60,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...theme.applyStyles("dark", {
    backgroundColor: theme.palette.grey[700],
  }),
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        backgroundImage: "linear-gradient(180deg, #13d6ce 0%, #039992 80%)",
        boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        backgroundImage: "linear-gradient(180deg, #13d6ce 0%, #039992 80%)",
      },
    },
  ],
}));

function ColorlibStepIcon(props: StepIconProps) {
  const { active, completed, className } = props;

  const icons: { [index: string]: React.ReactElement<any> } = {
    1: <img src={icon1} width={36} />,
    2: <img src={icon2} width={32} />,
    3: <img src={icon3} width={36} />,
    4: <img src={icon4} width={30} />,
  };

  return (
    <>
      <ColorlibStepIconRoot
        ownerState={{ completed, active }}
        className={className}
      >
        {icons[String(props.icon)]}
      </ColorlibStepIconRoot>
    </>
  );
}

const steps = [
  "Outbound Profile",
  "Case Studies",
  "Landing Page",
  "Engaging Content",
];

const Readme: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const { user } = useAuth();
  const simpleBarRef = useRef<any>(null);
  const isStepOptional = (step: number) => {
    return false;
  };

  const isStepSkipped = (step: number) => {
    return skipped.has(step);
  };

  useEffect(() => {
    setLoading(true);
    getData();
    setLoading(false);
  }, []);

  const getData = async () => {
    const cic = await getCicProfileById(user?.id);
    if (user?.role == "admin") {
      setActiveStep(Number(cic?.activeStep)+1);
    }
  };

  const handleNext = async () => {
    // const currentStepFields = getStepFields(activeStep);
    // const isValid = currentStepFields;
    // if (!isValid) return;
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped(newSkipped);

    if (simpleBarRef.current) {
      simpleBarRef.current
        .getScrollElement()
        .scrollTo({ top: 0, behavior: "smooth" });
    }

    console.log(activeStep);
    if (user?.role !== "admin") {
      const formData = {
        id: user?.id,
        user_id: user?.id,
        status: activeStep < 2 ? "draft" : "completed",
        activeStep,
      };
      await upsertCicProfile(formData);
    }
    // addProfile();
  };

  const handleConfirm = async () => {
    setLoading(true);
    const formData = {
      id: user?.id,
      user_id: user?.id,
      status: activeStep < 2 ? "draft" : "completed",
    };
    await upsertCicProfile(formData);
    await addNotification("Cic Confirmed", `${user?.email} have read cic`);
    navigate(-1);
    setLoading(false);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = async () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <div>
            <h4 className="text-lg font-bold mb-5">
              Building Block 1: Define Outbound Profile
            </h4>
            <p className="text-[#3F3F46] text-sm">
              The outbound profile is a crucial component of your initial
              contact strategy. It ensures clear and targeted communication with
              potential customers. The right profile can make the difference
              between a successful first contact and a missed opportunity.
            </p>
            <h5 className="font-semibold my-5">
              1.1 Define Status Delta and Build Relationships
            </h5>
            <h5 className="font-semibold my-5">What is Status Delta?</h5>
            <p className="text-[#3F3F46]">
              Status Delta describes the change or improvement that your product
              or service can bring to the customer. It is the difference between
              the customer's current state and the desired future state that
              your offering enables.
            </p>
            <h5 className="font-semibold my-5">How to Define Status Delta:</h5>
            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Analyze the Current State of Your Target Audience:
            </h5>
            <p className="text-[#3F3F46]">
              Research the situation your target customers are currently in.
              What challenges and problems are they facing? This information can
              be gathered through market research, customer interviews, or
              feedback from existing customers.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Define the Desired State:
            </h5>
            <p className="text-[#3F3F46]">
              Identify the goals and improvements your target customers want to
              achieve. This could include increased efficiency, cost reduction,
              or improved customer satisfaction.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Establish the Connection:
            </h5>
            <p className="text-[#3F3F46]">
              Clearly and precisely articulate how your product or service can
              create this Status Delta. Use concrete examples and data to
              illustrate the impact.
            </p>
            <h5 className="font-semibold my-5">
              Building and Maintaining Relationships:
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Find Relevant Touchpoints:
            </h5>

            <p className="text-[#3F3F46]">
              Research specific information about the contact person and their
              company to identify common interests or current challenges. Use
              these touchpoints in your communication to create immediate
              relevance and capture the recipient's interest.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Leverage LinkedIn and Other Relevant Networks:
            </h5>

            <p className="text-[#3F3F46]">
              Connect with potential customers on social media platforms.
              Research which posts and topics are of interest to your target
              audience and actively participate in those discussions.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              {" "}
              Step 3: Develop a Follow-up Strategy:
            </h5>

            <p className="text-[#3F3F46]">
              Plan regular follow-ups to strengthen the relationship. Ensure
              that your messages consistently provide value, such as offering
              useful information or resources, which could include newsletters.
            </p>
            <h5 className="font-semibold my-5">1.2 Define Titles</h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is the Title Important?
            </h5>

            <p className="text-[#3F3F46]">
              The title of your message is the first thing your potential
              customer sees. It determines whether your message will be opened
              and read.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Define an Effective Title:
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Be Clear and Concise:
            </h5>
            <p className="text-[#3F3F46]">
              Avoid long and complicated titles. Your title should convey the
              main benefit or purpose of your message in just a few words.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Create Relevance and Urgency:
            </h5>
            <p className="text-[#3F3F46]">
              Use terms that are relevant to your target person and suggest a
              sense of urgency. Examples might include: "Boost Your Efficiency
              with [Your Solution]" or "Don't Miss This Cost Reduction
              Opportunity."
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Test Different Variants:
            </h5>
            <p className="text-[#3F3F46]">
              Experiment with different titles and analyze open rates. Use A/B
              testing to find out which formulations work best.
            </p>
            <h5 className="font-semibold my-5">
              1.3 Update Social Media Profiles
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Are Social Media Profiles Important?
            </h5>

            <p className="text-[#3F3F46]">
              Your social media profiles are often the first point of contact
              for potential customers to learn more about you and your company.
              A professional and up-to-date profile strengthens your credibility
              and increases the chances of successful contact.
            </p>
            <h5 className="font-semibold my-5">How to Update Your Profiles:</h5>
            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Ensure Consistent Brand Presence:
            </h5>
            <p className="text-[#3F3F46]">
              Check that all your profiles (LinkedIn, Twitter, etc.) are
              consistently designed and contain up-to-date information. Your
              profile picture, header, and company description should be
              consistent across all platforms.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Share Relevant Content:
            </h5>
            <p className="text-[#3F3F46]">
              Regularly post content that underscores your expertise in your
              industry. This could be blog articles, whitepapers, or case
              studies. Ensure that the content aligns with the interests of your
              target audience.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Increase Engagement:
            </h5>
            <p className="text-[#3F3F46]">
              Actively participate in discussions in your industry. Comment on
              and share relevant posts and network with influencers and
              decision-makers in your field.
            </p>
            <h5 className="font-semibold my-5">
              1.4 Craft Effective Call Introductions
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is a Good Introduction Important?
            </h5>

            <p className="text-[#3F3F46]">
              The introduction to your call lays the foundation for the entire
              conversation. It must pique the interest of the person you're
              speaking to and immediately convey why it's worth their time to
              listen to you.
            </p>
            <h5 className="font-semibold my-5">
              How to Create a Convincing Introduction:
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Personalize Your Approach:
            </h5>
            <p className="text-[#3F3F46]">
              Start the conversation with a personalized greeting and refer to
              something relevant to the person you're speaking to (e.g., a
              current challenge you have researched). Tip: Start with a
              provocative question or an impressive statistic.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Use the Status Delta:
            </h5>
            <p className="text-[#3F3F46]">
              In a few sentences, express the benefits your product or service
              offers and how it can improve the situation of the person you're
              speaking to. It's essential to know the specific problem in the
              target market and clearly articulate it.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Provide a Clear Call to Action:
            </h5>
            <p className="text-[#3F3F46]">
              Conclude the introduction with a clear question or prompt that
              encourages the person to engage further with you. Example: "Would
              you be interested in learning more about how we've solved similar
              challenges for companies like yours?"
            </p>
          </div>
        );
      case 1:
        return (
          <div>
            <h4 className="text-lg font-bold mb-5">
              Building Block 2: Gather & Optimize Case Studies
            </h4>
            <p className="text-[#3F3F46] text-sm">
              Case studies are a powerful tool to demonstrate the value and
              effectiveness of your products or services to potential customers.
              They showcase concrete results and build trust by presenting real
              success stories from your existing customers.
            </p>
            <h5 className="font-semibold my-5">
              2.1 Describe the Before State
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is the Before State Important?
            </h5>

            <p className="text-[#3F3F46]">
              The before state provides potential customers with insight into
              the challenges your previous customers faced before using your
              solution. It represents the starting point from which your
              solution has driven positive change.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Describe the Before State:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Identify Common Problems:
            </h5>
            <p className="text-[#3F3F46]">
              Research typical challenges and issues your target audience faces.
              These could be identified through customer feedback, surveys, or
              interviews.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Use Specific Details:
            </h5>
            <p className="text-[#3F3F46]">
              Describe the before state in as much detail as possible. What
              processes or systems were not functioning optimally? What impact
              did these problems have on the company? Numbers and facts are
              particularly helpful here.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Create an Emotional Connection:
            </h5>
            <p className="text-[#3F3F46]">
              Highlight not only the rational problems but also the emotional
              aspects such as stress, uncertainty, or frustration that your
              customers experienced with their old processes. This increases the
              readers' identification with the situations described.
            </p>
            <h5 className="font-semibold my-5">2.2 Describe the After State</h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is the After State Important?
            </h5>

            <p className="text-[#3F3F46]">
              The after state illustrates the success of your solution. It shows
              how the customer's situation has improved and provides solid
              evidence of the benefits your products or services offer.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Describe the After State:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Highlight Concrete Results:
            </h5>
            <p className="text-[#3F3F46]">
              List specific measurable improvements achieved through your
              solution. Examples include: increased efficiency by X%, reduced
              costs by Y%, or improved customer satisfaction by Z points.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Use Visual Evidence:
            </h5>
            <p className="text-[#3F3F46]">
              Complement your case study with diagrams, graphics, or
              before-and-after images that visually highlight the results
              achieved. This makes the successes even more tangible.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Incorporate Positive Customer Feedback:
            </h5>
            <p className="text-[#3F3F46]">
              Directly quote customer reviews or testimonials where your
              customers confirm the benefits of your solution. This increases
              the credibility and authenticity of your case study.
            </p>
            <h5 className="font-semibold my-5">
              2.3 Summarize Results in Numbers
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is Quantitative Summarization Important?
            </h5>

            <p className="text-[#3F3F46]">
              Numbers and facts are crucial for making the value of your
              solution immediately apparent. They provide an objective basis for
              potential customers to evaluate your solution.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Summarize the Results:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Identify Key Metrics:
            </h5>
            <p className="text-[#3F3F46]">
              Determine the most important metrics that interest your target
              audience. These could include revenue increases, cost savings,
              efficiency gains, time savings, or other relevant KPIs.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Before-and-After Comparisons:
            </h5>
            <p className="text-[#3F3F46]">
              Show the improvement in the identified metrics before and after
              implementing your solution. Use percentages or absolute values to
              clearly demonstrate the change.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Quantify Success Stories:
            </h5>
            <p className="text-[#3F3F46]">
              If possible, specify how much revenue your customer generated
              through the use of your solution or how many hours of work were
              saved. These quantitative data should be concise and easy to
              understand.
            </p>
            <h5 className="font-semibold my-5">
              2.4 Conduct Edit and Summarize Videos with the Target Audience
            </h5>
            <h5 className="font-semibold mt-5 mb-2">Why Use Videos?</h5>

            <p className="text-[#3F3F46]">
              Videos offer a dynamic way to tell success stories and emotionally
              engage potential customers. They make complex issues more
              understandable and create a stronger connection with the viewer.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Create Effective Videos:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Recruit Customers for Video Testimonials:
            </h5>
            <p className="text-[#3F3F46]">
              Choose satisfied customers willing to share their positive
              experiences with your product. Prepare them for the conversation
              and help them clearly articulate the key points.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Professionally Edit Videos:
            </h5>
            <p className="text-[#3F3F46]">
              Ensure that your videos are professionally edited. Add text
              overlays to highlight important points and use appealing music to
              enhance the mood.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Summarize and Distribute:
            </h5>
            <p className="text-[#3F3F46]">
              Concisely summarize the key insights from the videos and integrate
              them into your marketing materials. Share the videos across your
              website, social media channels, and email campaigns to maximize
              reach.
            </p>
          </div>
        );
      case 2:
        return (
          <div>
            <h4 className="text-lg font-bold mb-5">
              Building Block 3: Create a Landing Page
            </h4>
            <p className="text-[#3F3F46] text-sm">
              A well-designed landing page is crucial for capturing the
              attention of potential customers and getting them to take the
              desired action. Your landing page should be clear, convincing, and
              visually appealing to make the best first impression.
            </p>
            <h5 className="font-semibold my-5">
              3.1 Create a Concise and Engaging Tagline
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is a Tagline Important?
            </h5>

            <p className="text-[#3F3F46]">
              The tagline is the first thing visitors notice on your landing
              page. It must immediately convey the value and benefits of your
              offer to capture the visitors' interest and keep them engaged.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Create an Effective Tagline:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Define Your Core Message:
            </h5>
            <p className="text-[#3F3F46]">
              Identify the main benefit of your product or service. What is the
              most important promise you can make to your customers? This
              message should be clearly and prominently communicated in your
              tagline.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Be Short and Concise:
            </h5>
            <p className="text-[#3F3F46]">
              A good tagline is brief, direct, and to the point. Try to express
              your message in one sentence or less. Examples might include:
              "Increase Your Efficiency by 50% with [Your Solution]" or "Your
              Solution for Seamless B2B Communication."
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Conduct A/B Testing:
            </h5>
            <p className="text-[#3F3F46]">
              Test different versions of your tagline to see which one resonates
              best with your target audience. Use analytics tools to compare the
              performance of different versions.
            </p>
            <h5 className="font-semibold my-5">
              3.2 Add Relevant Videos and Animations
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Are Visual Elements Important?
            </h5>

            <p className="text-[#3F3F46]">
              Videos and animations increase engagement on your landing page.
              They can convey complex information in a simple and engaging way
              and keep visitors on your page longer.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Integrate Videos and Animations:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Create Product Videos:
            </h5>
            <p className="text-[#3F3F46]">
              Create short informative videos that show your product or service
              in action. These videos should clearly present the core benefits
              and key features and demonstrate how your offering can help
              potential customers.
            </p>
            <h5 className="font-semibold mt-5 mb-2">Step 2: Use Animations:</h5>
            <p className="text-[#3F3F46]">
              Use animations to highlight key information or visualize complex
              processes. This can improve the user experience and make your
              offering easier to understand.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Strategically Place Videos:
            </h5>
            <p className="text-[#3F3F46]">
              Place your videos and animations prominently on the landing page,
              ideally directly under the tagline or in a separate section that
              highlights the benefits of your product. Ensure that the videos
              are easily accessible and highly visible.
            </p>
            <h5 className="font-semibold my-5">
              3.3 Integrate Logos and Case Studies
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Use Logos and Case Studies?
            </h5>

            <p className="text-[#3F3F46]">
              Logos of well-known clients and case studies build trust and
              credibility. They show potential customers that you are already
              successful in your industry and that your product is valued by
              others.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Integrate These Elements:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Add Your Clients' Logos:
            </h5>
            <p className="text-[#3F3F46]">
              Add the logos of companies you have successfully worked with.
              Place these logos in a section like "Our Clients" or "Trusted By"
              to underscore the credibility of your brand.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Create Short Case Studies:
            </h5>
            <p className="text-[#3F3F46]">
              Present a selection of case studies on your landing page. Each
              case study should include a brief description of the
              before-and-after state, the results achieved, and a customer quote
              that confirms the value of your solution.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Provide Links to Detailed Case Studies:
            </h5>
            <p className="text-[#3F3F46]">
              Offer links to more detailed case studies that take visitors to a
              separate page where they can delve deeper into the success
              stories. This can further convince interested parties and prompt
              them to take action.
            </p>
            <h5 className="font-semibold my-5">
              3.4 Define Clear Calls to Action
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Are Calls to Action (CTAs) Important?
            </h5>

            <p className="text-[#3F3F46]">
              CTAs guide visitors to the next steps on your landing page. They
              are crucial for maximizing conversion rates and encouraging
              visitors to take the desired action, whether it be requesting a
              demo, contacting you, or downloading a whitepaper.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Define Effective CTAs:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Clarify the Goal of the Landing Page:
            </h5>
            <p className="text-[#3F3F46]">
              Add the logos of companies you have successfully worked with.
              Place these logos in a section like "Our Clients" or "Trusted By"
              to underscore the credibility of your brand.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Prominent Placement:
            </h5>
            <p className="text-[#3F3F46]">
              Place your CTAs in prominent spots on the page, such as at the top
              of the page, in the middle of the content, or at the end of a
              section. They should be easy to find and visually highlighted.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Use Direct and Active Language:
            </h5>
            <p className="text-[#3F3F46]">
              Use clear, action-oriented phrases like "Request a Demo Now," "Try
              for Free," or "Learn More." These prompts should emphasize the
              benefit of the action and give the visitor a clear incentive to
              act.
            </p>
            <h5 className="font-semibold my-5">3.5 Link to the Main Site</h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Link to the Main Site?
            </h5>

            <p className="text-[#3F3F46]">
              Linking to the main site provides visitors with additional
              opportunities to learn about your company and discover more of
              your offerings. This can help build trust and provide more
              information about your services and products.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Create an Effective Link:
            </h5>

            <h5 className="font-semibold mt-5 mb-2">
              Step 1: Ensure Visible Navigation:
            </h5>
            <p className="text-[#3F3F46]">
              Integrate a clear and easily accessible navigation that seamlessly
              guides the visitor from the landing page to your main site. This
              could be a menu at the top of the page or links in the footer.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 2: Set Contextual Links:
            </h5>
            <p className="text-[#3F3F46]">
              Add links in relevant sections of your landing page that point to
              deeper information on your main site. For example, a link in a
              section about your services could lead to the corresponding
              subpage.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              Step 3: Maintain Consistent Design:
            </h5>
            <p className="text-[#3F3F46]">
              Ensure that the design of your landing page and main site is
              consistent. This creates a unified user experience and facilitates
              the transition between pages.
            </p>
          </div>
        );
      case 3:
        return (
          <div>
            <h4 className="text-lg font-bold mb-5">
              Building Block 4: Update Engaging Content
            </h4>
            <p className="text-[#3F3F46] text-sm">
              Regularly updating and maintaining your content is essential to
              continuously inform, engage, and convince your target audience.
              Well-prepared and relevant content strengthens your position as an
              expert in your industry and supports your sales and marketing
              activities.
            </p>

            <h5 className="font-semibold my-5">
              4.1 Create/Update Whitepapers
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Are Whitepapers Important?
            </h5>
            <p className="text-[#3F3F46]">
              Whitepapers are in-depth, informative documents that provide
              potential customers with valuable insights into your industry or
              specific topics. They serve not only as an educational resource
              but also as a means to demonstrate your knowledge and expertise.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Create and Update Whitepapers:
            </h5>
            <ul className="text-[#3F3F46] list-disc ml-5">
              <li>Step 1: Identify Relevant Topics</li>
              <p className="ml-5">
                Choose topics of great interest to your target audience where
                you can position yourself as an expert. These could be current
                trends, challenges, or solutions in your industry.
              </p>
              <li>Step 2: Establish Structure and Content</li>
              <p className="ml-5">
                Develop a clear structure covering key points: introduction,
                problem description, solutions, case examples, and conclusion.
                Ensure the content is well-researched and substantiated.
              </p>
              <li>Step 3: Regularly Update</li>
              <p className="ml-5">
                Keep your whitepapers up to date by adding new data, trends, and
                case studies regularly to ensure relevance.
              </p>
            </ul>

            <h5 className="font-semibold my-5">
              4.2 Create/Update Marketing Materials
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Is Current Marketing Material Important?
            </h5>
            <p className="text-[#3F3F46]">
              Updated marketing materials are crucial to effectively communicate
              the key benefits and unique selling points of your offering. They
              ensure that your potential customers receive the latest and most
              relevant information.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Create and Update Marketing Materials:
            </h5>
            <ul className="text-[#3F3F46] list-disc ml-5">
              <li>Step 1: Identify Key Messages</li>
              <p className="ml-5">
                Define the key messages that your marketing materials should
                convey. Highlight the benefits of your product or service and
                align them with the needs of your target audience.
              </p>
              <li>Step 2: Optimize Visual Design</li>
              <p className="ml-5">
                Ensure an appealing visual design that reflects your brand
                identity. Use high-quality images, graphics, and a consistent
                color scheme for a professional look.
              </p>
              <li>Step 3: Regular Review and Adaptation</li>
              <p className="ml-5">
                Regularly review your marketing materials to ensure all
                information is up to date and reflects the latest developments.
              </p>
            </ul>

            <h5 className="font-semibold my-5">
              4.3 Create Blogs and Define Additional Content
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why Are Blogs and Additional Content Important?
            </h5>
            <p className="text-[#3F3F46]">
              Blogs and other content formats like videos, infographics, or
              podcasts help maintain continuous engagement with your audience
              and strengthen your online presence. They are also essential for
              improving search engine visibility.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Create and Define Relevant Content:
            </h5>
            <ul className="text-[#3F3F46] list-disc ml-5">
              <li>Step 1: Plan Topics and Conduct Keyword Research</li>
              <p className="ml-5">
                Plan blog topics in advance and perform keyword research to
                optimize content for search engines and audience interests.
              </p>
              <li>Step 2: Regular Content Creation</li>
              <p className="ml-5">
                Create blog posts and content formats regularly to keep your
                audience informed and engaged with valuable, easy-to-understand
                content.
              </p>
              <li>Step 3: Diversify Content Formats</li>
              <p className="ml-5">
                Use various content formats (e.g., infographics, videos,
                whitepapers) to cater to different audience preferences and
                maximize content reach.
              </p>
            </ul>

            <h5 className="font-semibold my-5">
              4.4 Continuous Improvement and Feedback Integration
            </h5>
            <h5 className="font-semibold mt-5 mb-2">
              Why is Continuous Improvement Important?
            </h5>
            <p className="text-[#3F3F46]">
              Continuous improvement ensures that your content stays relevant
              and aligns with your audience’s expectations. Feedback allows you
              to refine your materials to better serve your customers' needs.
            </p>
            <h5 className="font-semibold mt-5 mb-2">
              How to Continuously Improve Your Content:
            </h5>
            <ul className="text-[#3F3F46] list-disc ml-5">
              <li>Step 1: Collect Customer Feedback</li>
              <p className="ml-5">
                Gather feedback from customers via surveys, interviews, or
                website interaction analysis to improve content based on their
                input.
              </p>
              <li>Step 2: Analyze Content Performance</li>
              <p className="ml-5">
                Use analytics tools to measure content performance (e.g., page
                views, dwell time, conversion rates) and identify what's working
                or needs improvement.
              </p>
              <li>Step 3: Regularly Update and Improve Content</li>
              <p className="ml-5">
                Based on feedback and performance analysis, continuously
                optimize content to reflect trends and meet audience needs.
              </p>
            </ul>

            <p className="text-[#3F3F46] mt-5">
              If you have any questions or uncertainties when creating or
              reviewing your initial contact content, don't hesitate to book a
              session with an Aquisize expert. We are here to help you optimize
              your content and ensure that your messages achieve the desired
              effect.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  console.log(checked);


  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdf; // Use the imported PDF path
    link.download = 'Phase 4 Inhalt.pdf'; // Set the desired file name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      {loading ? (
        <LoadingScreen />
      ) : (
        <div>
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <h4 className="text-xl font-bold flex items-center">
              <ChevronLeft
                size={22}
                className="me-3 cursor-pointer"
                onClick={() => navigate(-1)}
              />
              Content for initial contact
            </h4>

            {user?.role !== "admin" && (
                      <Button
                        className="sm:mt-0 mt-4 bg-[#1CB5AF]"
                        onClick={() => handleDownload()}
                      >
                        {/* <IoAdd size={20} className="me-2" /> */}
                        Download
                      </Button>
                    )}
          </div>

          <div className="mt-8">
            <Box sx={{ width: "100%" }}>
              <Stack sx={{ width: "100%" }} spacing={4}>
                {/* <Stepper
                  alternativeLabel
                  activeStep={activeStep}
                  connector={<QontoConnector />}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel StepIconComponent={QontoStepIcon}>
                        {label}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper> */}
                <Stepper
                  alternativeLabel
                  activeStep={activeStep}
                  connector={<ColorlibConnector />}
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel StepIconComponent={ColorlibStepIcon}>
                       <span className="font-bold">{label}</span> 
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Stack>
              {activeStep === steps.length ? (
                <React.Fragment>
                  <Typography sx={{ mt: 2, mb: 1 }}>
                    All steps completed - you&apos;re finished
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }} />
                    <Button onClick={handleReset}>Reset</Button>
                  </Box>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <div className="bg-white rounded-xl p-8 mt-5">
                    <SimpleBar ref={simpleBarRef} style={{ maxHeight: 330 }}>
                      {renderStepContent(activeStep)}
                    </SimpleBar>
                  </div>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      pt: 2,
                    }}
                  >
                    {activeStep !== 0 && (
                      <Button className="bg-[#1CB5AF]" disabled={activeStep === 0} onClick={handleBack}>
                        <ArrowLeft className="me-2" size={20} />
                        Building Block {activeStep}
                      </Button>
                    )}
                    <Box sx={{ flex: "1 1 auto" }} />
                    {isStepOptional(activeStep) && (
                      <Button color="inherit" onClick={handleSkip}>
                        Skip
                      </Button>
                    )}
                    {activeStep === steps.length - 1 ? (
                      // <Button type="submit">Save</Button>
                      <div></div>
                    ) : (
                      activeStep < 3 && (
                        <Button type="button" className="bg-[#1CB5AF]" onClick={handleNext}>
                          Building Bock {activeStep + 2}
                          <ArrowRight className="ms-2" size={20} />
                        </Button>
                      )
                    )}
                  </Box>
                  {user?.role !== "admin" && activeStep > 2 && (
                    <div className="flex justify-between mt-4 ">
                      <div className="flex items-center">
                        <Checkbox
                          value={checked}
                          onChange={(e) => {
                            setChecked(e.target.checked);
                            console.log(checked);
                          }}
                        />
                        <p>
                          I have read and understood the contents needed for a
                          successful first contact
                        </p>
                      </div>
                      <Button
                        type="button"
                        disabled={!checked}
                        onClick={handleConfirm}
                        className="bg-[#1CB5AF]"
                      >
                        Confirm
                        {/* <ArrowRight className="ms-2" size={20} /> */}
                      </Button>
                    </div>
                  )}
                </React.Fragment>
              )}
            </Box>
          </div>
        </div>
      )}
    </div>
  );
};

export default Readme;
